import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useParams } from 'react-router-dom';
import { TABLES, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  computeStatistics,
  linearRegression,
  loadStatisticsData,
  MONTH_LABELS,
  schoolMonthIndex,
} from '../lib/statistics';

const SummaryCard = ({ label, value, suffix }) => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="h4" fontWeight="bold">
        {value}{suffix || ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

export default function StatisticsStudent() {
  const { id } = useParams();
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedYear || selectedYear === 'all') return undefined;
    let mounted = true;
    setLoading(true);
    loadStatisticsData(selectedYear)
      .then((result) => { if (mounted) setData(result); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [selectedYear]);

  const stats = useMemo(() => (data ? computeStatistics(data) : null), [data]);
  const summary = useMemo(
    () => stats?.students.find((s) => s.id === id) || null,
    [stats, id],
  );

  const studentRequests = useMemo(
    () => (data?.requests || []).filter((r) => r.id_elev === id),
    [data, id],
  );

  const monthly = useMemo(() => {
    const credits = new Array(10).fill(0);
    studentRequests
      .filter((r) => r.status === 'validat')
      .forEach((r) => {
        const index = schoolMonthIndex(r.data_validare || r.data_cerere);
        if (index >= 0 && index < 10) credits[index] += r.credite_acordate || r.credite_solicitate || 0;
      });
    let running = 0;
    return MONTH_LABELS.map((luna, index) => {
      running += credits[index];
      return { luna, credite: credits[index], cumulat: running };
    });
  }, [studentRequests]);

  if (!selectedYear || loading || !stats || !summary) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const { slope } = linearRegression(monthly.map((point) => point.cumulat));
  const lastIndex = Math.min(9, stats.currentMonthIndex);
  const predicted = Math.max(
    monthly[lastIndex].cumulat,
    monthly[lastIndex].cumulat + slope * (9 - lastIndex),
  );

  const moduleData = (data.modules || []).map((m) => ({
    cod: m.cod_module,
    credite: summary.modules?.[m.cod_module] || 0,
    tinta: m.credite_tinta || 0,
  }));

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">{summary.nume} {summary.prenume}</Typography>
          <Typography variant="body2" color="text.secondary">
            Clasa {summary.clasa || '-'} • {summary.specializare || '-'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
          <SchoolYearBadge year={selectedYear} />
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>Export PDF</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <SummaryCard label="Credite totale" value={summary.total} />
        <SummaryCard label="Ranking în clasă" value={`${summary.rank}/${summary.classSize}`} />
        <SummaryCard label="Curriculare" value={summary.curricular} />
        <SummaryCard label="Extracurriculare" value={summary.extracurricular} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Credite cumulate pe lună
            </Typography>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="luna" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="cumulat" stroke="#4F46E5" name="Cumulat" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Credite per modul vs țintă
            </Typography>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={moduleData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cod" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="credite" fill="#4F46E5" name="Credite" />
                  <Bar dataKey="tinta" fill="#000000" name="Țintă" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Distribuție categorii
            </Typography>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={['curricular', 'extracurricular', 'transferabil'].map((key) => ({
                      name: CATEGORY_LABELS[key],
                      value: summary[key],
                    }))}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    label
                  >
                    {['curricular', 'extracurricular', 'transferabil'].map((key) => (
                      <Cell key={key} fill={CATEGORY_COLORS[key]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Predicție
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              La ritmul actual ({slope.toFixed(1)} credite/lună), vei atinge aproximativ{' '}
              <strong>{Math.round(predicted)} credite</strong> până în iunie.
            </Typography>
            <Chip
              label={summary.status === 'ok' ? 'Pe ritm bun' : summary.status === 'sub' ? 'Sub media clasei' : 'La risc'}
              color={summary.status === 'ok' ? 'success' : summary.status === 'sub' ? 'warning' : 'error'}
            />
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Timeline cereri
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {studentRequests.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nu există cereri în anul selectat.
            </Typography>
          )}
          {[...studentRequests]
            .sort((a, b) => new Date(b.data_cerere) - new Date(a.data_cerere))
            .map((request) => (
              <Box key={request.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <Box>
                  <Typography variant="body2">{request.nume_activitate}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(request.data_cerere).toLocaleDateString('ro-RO')}
                    {request.id_module ? ` • modul ${request.id_module}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Chip
                    size="small"
                    label={request.status}
                    color={request.status === 'validat' ? 'success' : request.status === 'respins' ? 'error' : 'warning'}
                  />
                  <Typography variant="caption" display="block">
                    {request.credite_acordate || request.credite_solicitate || 0} credite
                  </Typography>
                </Box>
              </Box>
            ))}
        </CardContent>
      </Card>
    </Container>
  );
}
